import { fromDateInputToJsDate } from './../../utils/date/fromDateInputToJsDate.util';
import { fromDatetimeInputToISO } from '@/common/utils/date/fromDatetimeInputToISO.util';
import { fromISOtoDate } from '@/common/utils/date/fromISOtoDate.util';
import { isValid } from '@/common/utils/validation/common.utils';
import { Injectable } from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { DateTime, DurationUnits } from 'luxon';

export const REG_EXP_WITHOUT_SPECIAL_CHAR_NUMBER = /^[a-zA-Z\s]+$/;
export const REG_EXP_EMAIL = /^([\w-.]+@([\w-]+\.)+[\w-]{2,4})?$/;

@Injectable({
    providedIn: 'root',
})
export class ValidationService {
    constructor(private readonly translate: TranslateService) {}

    public withoutSpecialCharNumber(
        control: FormControl,
    ): { [s: string]: boolean } | null {
        if (typeof control.value !== 'string' || control.value.length === 0) {
            return null;
        }
        if (!RegExp(REG_EXP_WITHOUT_SPECIAL_CHAR_NUMBER).exec(control.value)) {
            return { specialCharOrNumberNotAllowed: true };
        }
        return null;
    }

    public mismatchingValue(toMistmachValue: string): ValidatorFn {
        return (c: AbstractControl) => {
            if (c == null || c.value == null) return null;
            if (c.value !== toMistmachValue) {
                return null;
            }
            return {
                matchedValues: {
                    currentValue: c.value,
                    toMistmachValue: toMistmachValue,
                },
            };
        };
    }

    public matchingControls(passwordKey: string, confirmPasswordKey: string) {
        return (group: FormGroup): { [s: string]: unknown } | null => {
            const password = group.controls[passwordKey];
            const confirmPassword = group.controls[confirmPasswordKey];
            if (!_.isEqual(password.value, confirmPassword.value)) {
                return {
                    mismatched: {
                        currentControl: this.translate.instant(
                            'fe_core_formControlName_' + passwordKey,
                        ),
                        currentValue: password.value,
                        toMatchControl: this.translate.instant(
                            'fe_core_formControlName_' + confirmPasswordKey,
                        ),
                        toMatchValue: confirmPassword.value,
                    },
                };
            }
            return null;
        };
    }

    public mismatchingControls(
        passwordKey: string,
        confirmPasswordKey: string,
    ) {
        return (group: FormGroup): { [s: string]: unknown } | null => {
            const password = group.controls[passwordKey];
            const confirmPassword = group.controls[confirmPasswordKey];
            if (!isValid(password.value) || !isValid(confirmPassword.value)) {
                return null;
            }
            if (_.isEqual(password.value, confirmPassword.value)) {
                return {
                    matched: {
                        currentControl: this.translate.instant(
                            'fe_core_formControlName_' + passwordKey,
                        ),
                        currentValue: password.value,
                        toMismatchControl: this.translate.instant(
                            'fe_core_formControlName_' + confirmPasswordKey,
                        ),
                        toMismatchValue: confirmPassword.value,
                    },
                };
            }
            return null;
        };
    }

    public isGreaterThanDatetime(
        startDate: string,
        startHour: string,
        endDate: string,
        endHour: string,
    ) {
        return (group: FormGroup): { [s: string]: unknown } | null => {
            const startDateValue = group.controls[startDate]?.value;
            const startHourValue = group.controls[startHour]?.value;
            const endDateValue = group.controls[endDate]?.value;
            const endHourValue = group.controls[endHour]?.value;

            if (
                !isValid(startDateValue) ||
                !isValid(startHourValue) ||
                !isValid(endDateValue) ||
                !isValid(endHourValue)
            ) {
                return null;
            }

            const startDatetime = fromDatetimeInputToISO(
                startDateValue,
                startHourValue,
            );
            const endtDatetime = fromDatetimeInputToISO(
                endDateValue,
                endHourValue,
            );

            if (
                DateTime.fromISO(startDatetime) <
                    DateTime.fromISO(endtDatetime) &&
                DateTime.fromISO(endtDatetime).diff(
                    DateTime.fromISO(startDatetime),
                    'hours',
                ).hours >= 1
            ) {
                return null;
            }

            return {
                lessThanDate: {
                    currentControl: this.translate.instant(
                        'fe_core_formControlName_' + startDate,
                    ),
                    toGreaterThanDateControl: this.translate.instant(
                        'fe_core_formControlName_' + endDate,
                    ),
                },
            };
        };
    }

    public maxDifferenceDates(
        startDate: string,
        endDate: string,
        difference: number,
        durationUnits: DurationUnits = 'days',
    ) {
        return (group: FormGroup): { [s: string]: unknown } | null => {
            const startDateValue = group.controls[startDate]?.value;
            if (!isValid(startDateValue)) {
                return null;
            }
            const startDateD = fromDateInputToJsDate(startDateValue);
            const endDateValue = group.controls[endDate]?.value;
            if (!isValid(endDateValue)) {
                return null;
            }
            const endDateD = fromDateInputToJsDate(endDateValue);
            const differenceObject = DateTime.fromJSDate(endDateD)
                .diff(DateTime.fromJSDate(startDateD), durationUnits)
                .toObject();
            if (_.get(differenceObject, durationUnits) > difference) {
                return {
                    differenceDatesOut: {
                        difference: _.get(differenceObject, durationUnits),
                        toMaxDifference: difference,
                        units: difference,
                        currentControl: this.translate.instant(
                            'fe_core_formControlName_' + startDate,
                        ),
                        toMaxControl: this.translate.instant(
                            'fe_core_formControlName_' + endDate,
                        ),
                        durationUnits: this.translate.instant(
                            'fe_core_validation_service_duration-units-' +
                                durationUnits,
                        ),
                    },
                };
            }
            return null;
        };
    }

    public minDifferenceDates(
        startDate: string,
        startHour: string,
        endDate: string,
        endHour: string,
        difference: number,
        durationUnits: DurationUnits = 'days',
    ): ValidatorFn {
        return (group: AbstractControl): { [key: string]: unknown } | null => {
            const startDateValue = group.get(startDate)?.value;
            const startHourValue = group.get(startHour)?.value;
            const endDateValue = group.get(endDate)?.value;
            const endHourValue = group.get(endHour)?.value;

            if (
                !isValid(startDateValue) ||
                !isValid(startHourValue) ||
                !isValid(endDateValue) ||
                !isValid(endHourValue)
            ) {
                return null;
            }

            const startDateD = fromISOtoDate(
                fromDatetimeInputToISO(startDateValue, startHourValue),
            );
            const endDateD = fromISOtoDate(
                fromDatetimeInputToISO(endDateValue, endHourValue),
            );

            const differenceObject = DateTime.fromJSDate(endDateD)
                .diff(DateTime.fromJSDate(startDateD), durationUnits)
                .toObject();

            if (_.get(differenceObject, durationUnits) < difference) {
                return {
                    differenceDatesIn: {
                        difference: _.get(differenceObject, durationUnits),
                        toMinDifference: difference,
                        units: difference,
                        currentControl: this.translate.instant(
                            'fe_core_formControlName_' + startDate,
                        ),
                        toMinControl: this.translate.instant(
                            'fe_core_formControlName_' + endDate,
                        ),
                        durationUnits: this.translate.instant(
                            'fe_core_validation_service_duration-units-' +
                                durationUnits,
                        ),
                    },
                };
            }
            return null;
        };
    }

    /**
     * Valida si el campo es un email
     *
     * @param {FormControl} control
     * @returns {({[s: string]: boolean} | null)}
     * @memberof ValidationProvider
     */
    public validEmail(control: FormControl): { [s: string]: boolean } | null {
        if (!_.isString(control.value)) {
            return null;
        }
        if (!control.value.match(REG_EXP_EMAIL)) {
            return { invalidEmail: true };
        }
        return null;
    }

    public validPhone(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (!value) {
            return null;
        }
        return /^\d{10}$/.test(value) ? null : { phoneInvalid: true };
    }

    public ineValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            const isValid = /^[0-9]{9}$/.test(value);
            return isValid ? null : { ineInvalid: true };
        };
    }

    public passportValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            const isValid = /^[a-zA-Z0-9]{9}$/.test(value);
            return isValid ? null : { passportInvalid: true };
        };
    }

    public rfcValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            if (!value) {
                return null;
            }

            const isValid = /^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/.test(
                String(value).trim().toUpperCase(),
            );
            return isValid ? null : { rfcInvalid: true };
        };
    }
}
